import {
  AggregationFunction,
  AnalyticalVariableCriterion,
  CohortBuilderDefinitionLegacy,
  CriteriaSelection,
  Criterion,
  DerivedEventTypeLegacy,
  InputFieldLegacy,
  VariableOutputType,
  SubjectVariableCriteriaSelectionTypeLegacy,
} from '@verantos-dev/types-cohorts';

const formatIsSelected = (isSelected: InputFieldLegacy | undefined): InputFieldLegacy => {
  return isSelected && isSelected.value === 'true' ? isSelected : { value: 'false', id: '' };
};

const transformDerivedEvent = (
  event: CohortBuilderDefinitionLegacy['derivedEvents'][number]
): CohortBuilderDefinitionLegacy['derivedEvents'][number] => {
  return {
    ...event,
    eventFilters: event.eventFilters.map((filter) => ({
      ...filter,
      eventName: filter.eventName,
      eventDateAttribute: filter.eventDateAttribute,
      position:
        event.type === DerivedEventTypeLegacy.INSTANCE
          ? filter.position ?? { value: '', id: '' }
          : undefined,
      isSelected:
        event.type === DerivedEventTypeLegacy.SEQUENCE
          ? formatIsSelected(filter.isSelected)
          : undefined,
      index:
        filter.index &&
        event.type === DerivedEventTypeLegacy.INSTANCE &&
        filter.position?.value === 'INDEX'
          ? filter.index
          : undefined,
    })),
    temporalConstraints:
      event.type === DerivedEventTypeLegacy.SEQUENCE ? event.temporalConstraints : [],
    uniquenessFilters: event.type === DerivedEventTypeLegacy.MERGED ? event.uniquenessFilters : [],
  };
};

/**
 * Normalize criteria by converting snake_case field names to camelCase.
 * This handles data coming from Research Assistant or backend that may use snake_case.
 */
const normalizeCriteria = (
  criteria: CohortBuilderDefinitionLegacy['criteria']
): CohortBuilderDefinitionLegacy['criteria'] => {
  if (!criteria) {
    return undefined;
  }

  return criteria.map((criterion): Criterion => {
    if (!criterion || typeof criterion !== 'object') {
      return criterion as Criterion;
    }

    const criterionObj = criterion as Record<string, unknown>;

    // Get criteriaType from either camelCase or snake_case, normalize to snake_case value
    const criteriaTypeValue =
      ('criteriaType' in criterionObj && criterionObj.criteriaType) ||
      ('criteria_type' in criterionObj && criterionObj.criteria_type);

    // Normalize legacy 'analyticalVariable' to 'analytical_variable' for consistency
    const normalizedCriteriaType =
      criteriaTypeValue === 'analyticalVariable' ? 'analytical_variable' : criteriaTypeValue;

    // Check if this is an analytical variable criterion that needs normalization
    if (normalizedCriteriaType === 'analytical_variable') {
      const avCriterion = criterionObj;

      // Build normalized criterion with camelCase property names but snake_case values
      // Use 'analytical_variable' as the value to match backend expectations
      const normalized = {
        criteriaType: 'analytical_variable' as const,
        usageType: 'criterion' as const,
        analyticalVariableId: '',
        criteriaSelection: CriteriaSelection.INCLUSION, // Will be set below if present
      } as AnalyticalVariableCriterion;

      // Preserve or set usageType (convert from usage_type if needed)
      if (
        'usageType' in avCriterion &&
        avCriterion.usageType !== null &&
        avCriterion.usageType !== undefined
      ) {
        normalized.usageType = avCriterion.usageType as 'criterion';
      } else if (
        'usage_type' in avCriterion &&
        avCriterion.usage_type !== null &&
        avCriterion.usage_type !== undefined
      ) {
        normalized.usageType = avCriterion.usage_type as 'criterion';
      } else {
        // Default to 'criterion' if not present
        normalized.usageType = 'criterion';
      }

      // Convert analytical_variable_id to analyticalVariableId (also handle legacy base_id for backward compatibility)
      if ('analytical_variable_id' in avCriterion) {
        normalized.analyticalVariableId = String(avCriterion.analytical_variable_id);
      } else if ('analyticalVariableId' in avCriterion) {
        // Already in camelCase, preserve it
        normalized.analyticalVariableId = String(avCriterion.analyticalVariableId);
      } else if ('analytical_variable_base_id' in avCriterion) {
        // Legacy support for backward compatibility
        normalized.analyticalVariableId = String(avCriterion.analytical_variable_base_id);
      } else if ('analyticalVariableBaseId' in avCriterion) {
        // Legacy support for backward compatibility
        normalized.analyticalVariableId = String(avCriterion.analyticalVariableBaseId);
      }

      // Preserve criteriaSelection (convert snake_case to camelCase)
      // Always include criteriaSelection, defaulting to INCLUSION if not present
      if (
        'criteria_selection' in avCriterion &&
        avCriterion.criteria_selection !== null &&
        avCriterion.criteria_selection !== undefined
      ) {
        normalized.criteriaSelection = avCriterion.criteria_selection as CriteriaSelection;
      } else if (
        'criteriaSelection' in avCriterion &&
        avCriterion.criteriaSelection !== null &&
        avCriterion.criteriaSelection !== undefined
      ) {
        // Already in camelCase, preserve it
        normalized.criteriaSelection = avCriterion.criteriaSelection as CriteriaSelection;
      } else {
        // Default to INCLUSION if not present (matching backend default)
        normalized.criteriaSelection = CriteriaSelection.INCLUSION;
      }

      // Preserve sourceLibraryAvId (support both snake_case and camelCase)
      if ('source_library_av_id' in avCriterion && avCriterion.source_library_av_id != null) {
        normalized.sourceLibraryAvId = String(avCriterion.source_library_av_id);
      } else if ('sourceLibraryAvId' in avCriterion && avCriterion.sourceLibraryAvId != null) {
        normalized.sourceLibraryAvId = String(avCriterion.sourceLibraryAvId);
      }

      // Preserve optional usage config fields
      if (
        'aggregation' in avCriterion &&
        avCriterion.aggregation !== null &&
        avCriterion.aggregation !== undefined
      ) {
        normalized.aggregation = avCriterion.aggregation as AggregationFunction | null;
      } else if ('aggregation' in avCriterion) {
        normalized.aggregation = null;
      }
      if (
        'output_format' in avCriterion &&
        avCriterion.output_format !== null &&
        avCriterion.output_format !== undefined
      ) {
        normalized.outputFormat = avCriterion.output_format as VariableOutputType | null;
      } else if ('output_format' in avCriterion) {
        normalized.outputFormat = null;
      } else if (
        'outputFormat' in avCriterion &&
        avCriterion.outputFormat !== null &&
        avCriterion.outputFormat !== undefined
      ) {
        normalized.outputFormat = avCriterion.outputFormat as VariableOutputType | null;
      } else if ('outputFormat' in avCriterion) {
        normalized.outputFormat = null;
      }

      // Handle criteriaSelection (support both snake_case and camelCase)
      if (
        'criteria_selection' in avCriterion &&
        avCriterion.criteria_selection !== null &&
        avCriterion.criteria_selection !== undefined
      ) {
        normalized.criteriaSelection =
          avCriterion.criteria_selection as SubjectVariableCriteriaSelectionTypeLegacy;
      } else if (
        'criteriaSelection' in avCriterion &&
        avCriterion.criteriaSelection !== null &&
        avCriterion.criteriaSelection !== undefined
      ) {
        normalized.criteriaSelection =
          avCriterion.criteriaSelection as SubjectVariableCriteriaSelectionTypeLegacy;
      } else {
        // Default to Inclusion if not present
        normalized.criteriaSelection = SubjectVariableCriteriaSelectionTypeLegacy.INCLUSION;
      }

      return normalized;
    }

    // Check if this is a subject variable criterion (new format)
    if (criteriaTypeValue === 'subject_variable' || criteriaTypeValue === 'subjectVariable') {
      // Normalize snake_case to camelCase
      const normalized: Criterion = {
        criteriaType: 'subject_variable',
        subjectVariableId: '',
      };

      // Convert subject_variable_id to subjectVariableId
      if ('subject_variable_id' in criterionObj) {
        normalized.subjectVariableId = String(criterionObj.subject_variable_id);
      } else if ('subjectVariableId' in criterionObj) {
        // Already in camelCase, preserve it
        normalized.subjectVariableId = String(criterionObj.subjectVariableId);
      }

      return normalized;
    }

    // Handle legacy format: SubjectVariable object in criteria (old format)
    // Convert to SubjectVariableCriterion format
    if ('id' in criterionObj && !criteriaTypeValue) {
      // This looks like a SubjectVariable object, convert it to SubjectVariableCriterion
      const normalized: Criterion = {
        criteriaType: 'subject_variable',
        subjectVariableId: String(criterionObj.id),
      };
      return normalized;
    }

    // For other criteria types, return as-is
    return criterion as Criterion;
  });
};

export const transformer = (
  cohortDefinition: CohortBuilderDefinitionLegacy
): CohortBuilderDefinitionLegacy => {
  return {
    simpleEvents: cohortDefinition.simpleEvents,
    derivedEvents: cohortDefinition.derivedEvents.map((event) => transformDerivedEvent(event)),
    subjectVariables: cohortDefinition.subjectVariables,
    indexDate: cohortDefinition.indexDate,
    studyPeriodRestrictions: cohortDefinition.studyPeriodRestrictions,
    // FEATURE FLAG: Preserve analytical variables fields when present
    // These fields come from backend when analytical_variables_in_cohort_builder is enabled
    // CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag, ensure these fields are always preserved
    // Normalize null changeStatus/changedBy → undefined so Zod's .optional() accepts them.
    // Unchanged AVs from the backend have these fields as null rather than omitted.
    analyticalVariables: cohortDefinition.analyticalVariables?.map((av) => ({
      ...av,
      changeStatus: av.changeStatus ?? undefined,
      changedBy: av.changedBy ?? undefined,
    })),
    // Normalize criteria to ensure snake_case fields are converted to camelCase
    criteria: normalizeCriteria(cohortDefinition.criteria),
  };
};
